import React, { type ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { axiosGet, axiosPost } from '../../util/api'
import {
    BulkActionDialog,
    BulkActionDialogActions,
    BulkActionDialogResultActions,
    BulkActionField,
    bulkActionInputClassName,
} from '../ui/BulkActionDialog'
import { BulkActionResultDetails, BulkActionStatus } from '../ui/BulkActionStatus'
import {
    exceedsPortfolioLimit,
    parsePortfolioNameInput,
    PORTFOLIO_LIMIT,
    PORTFOLIO_NAME_MAX_LENGTH,
} from './portfolioNames'

export interface PortfolioCreateItemResult {
    name: string
    status: 'created' | 'failed' | 'skipped'
    portfolioId?: string
    reason?: string
}

export interface CreatePortfoliosModalProps {
    isOpen: boolean
    onClose: () => void
    onCreated: () => void
}

interface ExistingPortfolio {
    portfolio_id: string | number
    name: string
    state: number
}

const ARCHIVED_STATE = 3

type ExistingStatus = 'loading' | 'loaded' | 'failed'

const NameList = ({ label, names }: { label: string; names: string[] }): JSX.Element | null => {
    if (names.length === 0) {
        return null
    }

    return (
        <section className="mt-[18px] border-t border-[#d8e0e8] pt-[12px]">
            <h3 className="m-0 text-[13px] font-semibold text-[#1c2127]">
                {label} ({names.length})
            </h3>
            <ul className="m-0 mt-[6px] max-h-[96px] overflow-y-auto p-0 pl-[18px] text-[13px] leading-[19px] text-[#5f6b7c]">
                {names.map((name, index) => (
                    // The duplicates bucket can hold the same name twice.
                    <li key={`${name}-${index}`}>{name}</li>
                ))}
            </ul>
        </section>
    )
}

interface CreatePortfoliosModalViewProps {
    isOpen: boolean
    input: string
    existingStatus: ExistingStatus
    existingCount: number
    parsed: ReturnType<typeof parsePortfolioNameInput>
    overLimit: boolean
    loading: boolean
    canSubmit: boolean
    results: PortfolioCreateItemResult[] | null
    onClose: () => void
    onInputChange: (value: string) => void
    onFileChange: (event: ChangeEvent<HTMLInputElement>) => void
    onSubmit: () => void
}

export const CreatePortfoliosModalView = ({
    isOpen,
    input,
    existingStatus,
    existingCount,
    parsed,
    overLimit,
    loading,
    canSubmit,
    results,
    onClose,
    onInputChange,
    onFileChange,
    onSubmit,
}: CreatePortfoliosModalViewProps): JSX.Element => {
    const created = results?.filter((result) => result.status === 'created').length ?? 0
    const failed = results?.filter((result) => result.status === 'failed').length ?? 0
    const skipped = results?.filter((result) => result.status === 'skipped').length ?? 0
    // An empty response is a failure to report, not a partial success.
    const resultPhase =
        created > 0
            ? failed > 0
                ? 'partially_succeeded'
                : 'succeeded'
            : failed > 0 || results?.length === 0
              ? 'failed'
              : 'partially_succeeded'
    const resultTitle =
        created > 0
            ? failed > 0
                ? 'Some portfolios were created'
                : 'Portfolios created'
            : failed > 0
              ? 'Portfolios could not be created'
              : skipped > 0
                ? 'No portfolios were created'
                : 'No portfolio results were returned'
    // The title and the outcome line say everything; neither a refresh nor its
    // absence needs restating.
    const resultMessage = undefined
    const blockingValidations = [
        overLimit
            ? `Existing ${existingCount} + new ${parsed.create.length} exceeds the ${PORTFOLIO_LIMIT} portfolio limit.`
            : null,
        parsed.create.length > PORTFOLIO_LIMIT ? `Enter ${PORTFOLIO_LIMIT} names or fewer.` : null,
        parsed.tooLong.length > 0
            ? `${parsed.tooLong.length} name${parsed.tooLong.length === 1 ? '' : 's'} exceed${
                  parsed.tooLong.length === 1 ? 's' : ''
              } ${PORTFOLIO_NAME_MAX_LENGTH} characters.`
            : null,
    ].filter((validation): validation is string => validation !== null)

    return (
        <BulkActionDialog
            isOpen={isOpen}
            onClose={onClose}
            title={results === null ? 'Add portfolios' : 'Portfolio creation results'}
            description={
                results === null ? 'Existing and duplicate names will be skipped.' : undefined
            }
            width="default"
            dismissible={!loading}
            footer={
                results === null ? (
                    <BulkActionDialogActions
                        primaryLabel="Create portfolios"
                        loadingLabel="Creating…"
                        loading={loading}
                        disabled={!canSubmit}
                        onPrimary={onSubmit}
                        onCancel={onClose}
                    />
                ) : (
                    <BulkActionDialogResultActions onDismiss={onClose} />
                )
            }
        >
            {results === null ? (
                <>
                    {existingStatus === 'loading' && (
                        <BulkActionStatus
                            phase="running"
                            title="Loading existing portfolios"
                            message="Checking names and your remaining portfolio allowance."
                            className="mb-[18px]"
                        />
                    )}
                    <BulkActionField
                        label="Portfolio names"
                        htmlFor="portfolio-names"
                        description="Enter one name per line, or upload a .txt or .csv file."
                        required
                    >
                        <textarea
                            id="portfolio-names"
                            aria-label="Portfolio names"
                            className={`${bulkActionInputClassName} min-h-[190px] resize-y`}
                            rows={10}
                            value={input}
                            onChange={(event) => onInputChange(event.target.value)}
                            placeholder={'Autumn Titles\nBackfill Series'}
                        />
                    </BulkActionField>
                    <BulkActionField label="Upload names" htmlFor="portfolio-file">
                        <input
                            id="portfolio-file"
                            aria-label="Upload portfolio names"
                            className={`${bulkActionInputClassName} file:mr-[12px] file:rounded-[4px] file:border-0 file:bg-[#edeff2] file:px-[10px] file:py-[5px] file:text-[13px]`}
                            type="file"
                            accept=".txt,.csv"
                            onChange={onFileChange}
                        />
                    </BulkActionField>

                    {existingStatus === 'failed' && (
                        <p className="m-0 mt-[14px] text-[13px] leading-[19px] text-[#8a4f0f]">
                            We could not load your existing portfolios. Duplicate and limit checks
                            will run on the server.
                        </p>
                    )}
                    {blockingValidations.length > 0 && (
                        <section
                            role="alert"
                            aria-labelledby="portfolio-validation-summary"
                            className="mt-[18px] border-y border-[#d8e0e8] py-[12px]"
                        >
                            <h3
                                id="portfolio-validation-summary"
                                className="m-0 text-[14px] font-semibold text-[#c23030]"
                            >
                                Fix these issues before creating portfolios
                            </h3>
                            <ul className="m-0 mt-[7px] p-0 pl-[18px] text-[13px] leading-[20px] text-[#c23030]">
                                {blockingValidations.map((validation) => (
                                    <li key={validation}>{validation}</li>
                                ))}
                            </ul>
                        </section>
                    )}

                    <NameList
                        // Promising creation while submission is blocked was a
                        // straight contradiction of the validation list above.
                        label={
                            blockingValidations.length > 0 ? 'Ready to create' : 'Will be created'
                        }
                        names={parsed.create}
                    />
                    <NameList label="Duplicate lines collapsed" names={parsed.duplicates} />
                    <NameList label="Already exist, will be skipped" names={parsed.existing} />
                </>
            ) : (
                <>
                    <BulkActionStatus
                        phase={resultPhase}
                        verb="created"
                        entity="portfolio"
                        entityPlural="portfolios"
                        title={resultTitle}
                        message={resultMessage}
                        counts={{ succeeded: created, failed, skipped }}
                    />
                    <BulkActionResultDetails
                        title="Results"
                        verb="created"
                        items={results.map((result, index) => ({
                            id: result.portfolioId ?? `${result.status}-${result.name}-${index}`,
                            label: result.name,
                            status: result.status === 'created' ? 'succeeded' : result.status,
                            message: result.reason,
                        }))}
                    />
                </>
            )}
        </BulkActionDialog>
    )
}

export const CreatePortfoliosModal = ({
    isOpen,
    onClose,
    onCreated,
}: CreatePortfoliosModalProps): JSX.Element => {
    const [input, setInput] = useState('')
    const [existingNames, setExistingNames] = useState<string[]>([])
    const [existingStatus, setExistingStatus] = useState<ExistingStatus>('loading')
    const [loading, setLoading] = useState(false)
    const [results, setResults] = useState<PortfolioCreateItemResult[] | null>(null)

    useEffect(() => {
        if (!isOpen) {
            return
        }

        setExistingStatus('loading')
        setExistingNames([])

        // `amazon-portfolios/search` caps at 10 rows, so the skip preview and
        // the cap arithmetic both read the paginated browse endpoint instead.
        // Browse includes archived portfolios; the server counts only
        // non-archived ones, so drop them or the cap math over-counts.
        axiosGet('amazon-portfolios', { perPage: 1000 })
            .then((response) => {
                const rows: ExistingPortfolio[] = response?.data?.data ?? []
                setExistingNames(
                    rows.filter((row) => row.state !== ARCHIVED_STATE).map((row) => row.name),
                )
                setExistingStatus('loaded')
            })
            .catch(() => {
                setExistingNames([])
                setExistingStatus('failed')
            })
    }, [isOpen])

    const parsed = useMemo(
        () => parsePortfolioNameInput(input, existingNames),
        [input, existingNames],
    )

    const overLimit = exceedsPortfolioLimit(existingNames.length, parsed.create.length)
    // A pending fetch leaves `existingNames` empty, which would silently blank
    // the skip preview and the cap math. A failed one falls back to the
    // server-side checks instead of blocking the user.
    const canSubmit =
        existingStatus !== 'loading' &&
        !loading &&
        parsed.create.length > 0 &&
        parsed.create.length <= PORTFOLIO_LIMIT &&
        parsed.tooLong.length === 0 &&
        !overLimit

    const reset = useCallback(() => {
        setInput('')
        setResults(null)
        setLoading(false)
    }, [])

    const handleClose = useCallback(() => {
        reset()
        onClose()
    }, [onClose, reset])

    const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (!file) {
            return
        }

        const reader = new FileReader()
        reader.onload = () => setInput(String(reader.result ?? ''))
        reader.readAsText(file)
        // Allow re-selecting the same file after an edit.
        event.target.value = ''
    }

    const handleSubmit = () => {
        setLoading(true)
        axiosPost('bulk-actions/create-portfolios', { names: parsed.create })
            .then((response) => {
                const items: PortfolioCreateItemResult[] = response?.data?.items ?? []
                setLoading(false)
                setResults(items)
                if (items.some((item) => item.status === 'created')) {
                    onCreated()
                }
            })
            .catch(() => {
                // The shared axios handler already toasts the error message.
                setLoading(false)
            })
    }

    return (
        <CreatePortfoliosModalView
            isOpen={isOpen}
            input={input}
            existingStatus={existingStatus}
            existingCount={existingNames.length}
            parsed={parsed}
            overLimit={overLimit}
            loading={loading}
            canSubmit={canSubmit}
            results={results}
            onClose={handleClose}
            onInputChange={setInput}
            onFileChange={handleFile}
            onSubmit={handleSubmit}
        />
    )
}
